import { useState, useCallback } from 'react';
import { apiFetch } from '../../../auth/apiFetch';
import { getAccessToken, triggerReauth } from '../../../auth/AuthProvider';

// Files above this threshold can't pass through API Gateway (10 MB hard limit).
// Used only as a fallback guard when S3 is unavailable (local dev).
const MAX_DIRECT_SIZE = 9 * 1024 * 1024;

/**
 * Hook for counting images in a PDF file.
 *
 * Flow (production, USE_S3=true):
 *   1. POST /count-images-init  → presigned S3 PUT URL + s3_key
 *   2. PUT directly to S3       → bypasses API Gateway's 10 MB limit
 *   3. POST /count-images-s3    → SSE stream of progress + result
 *
 * Fallback (local dev, USE_S3=false, file ≤ 9 MB):
 *   POST /count-images with multipart/form-data
 *
 * @param {string} apiBaseUrl
 * @returns {{
 *   countImages: Function,
 *   isCountingImages: boolean,
 *   progress: (
 *     { phase: 'uploading', pct: number } |
 *     { phase: 'opening' } |
 *     { phase: 'processing', pagesDone: number, totalPages: number } |
 *     null
 *   )
 * }}
 */
function useImageCount(apiBaseUrl) {
  const [isCountingImages, setIsCountingImages] = useState(false);
  const [progress, setProgress] = useState(null);

  const countImages = useCallback(async (file) => {
    setIsCountingImages(true);
    setProgress({ phase: 'uploading', pct: 0 });
    const token = await getAccessToken();

    return new Promise((resolve, reject) => {
      const finish = (resultData) => {
        setIsCountingImages(false);
        setProgress(null);
        resolve(resultData);
      };

      const fail = (err) => {
        setIsCountingImages(false);
        setProgress(null);
        reject(err);
      };

      // ── SSE stream helper ────────────────────────────────────────────────
      // Sends `body` to `url` via XHR POST and reads the SSE response.
      // `contentType` is set as Content-Type when provided (omit for FormData).
      const streamSse = (url, body, contentType) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        if (contentType) xhr.setRequestHeader('Content-Type', contentType);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        let processedLength = 0;
        let result = null;

        xhr.onprogress = () => {
          const newChunk = xhr.responseText.slice(processedLength);
          processedLength = xhr.responseText.length;

          for (const line of newChunk.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            let data;
            try {
              data = JSON.parse(line.slice(6));
            } catch {
              continue;
            }

            if (data.type === 'progress') {
              setProgress({ phase: 'processing', pagesDone: data.pages_done, totalPages: data.total_pages });
            } else if (data.type === 'result') {
              result = data;
            } else if (data.type === 'error') {
              xhr.abort();
              fail(new Error(data.detail));
            }
          }
        };

        xhr.onload = () => {
          if (xhr.status === 401) {
            // Same handling as apiFetch.js: session is actually over,
            // redirect to login instead of surfacing a confusing error.
            triggerReauth();
            return;
          }
          if (xhr.status !== 200) {
            fail(new Error(`HTTP ${xhr.status}`));
            return;
          }
          if (!result) {
            fail(new Error('No result received from server'));
            return;
          }
          const { type, ...resultData } = result;
          finish(resultData);
        };

        xhr.onerror = () => fail(new Error('Network error'));
        xhr.send(body);
      };

      // ── Step 1: get a presigned S3 PUT URL ───────────────────────────────
      apiFetch(`${apiBaseUrl}/api/count-images-init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name }),
      })
        .then((initResp) => {
          if (initResp.ok) {
            return initResp.json().then(({ presigned_url, s3_key }) => {
              // ── Step 2: upload directly to S3 (no API Gateway in the path) ──
              const uploadXhr = new XMLHttpRequest();
              uploadXhr.open('PUT', presigned_url);
              uploadXhr.setRequestHeader('Content-Type', 'application/pdf');

              uploadXhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                  setProgress({ phase: 'uploading', pct: Math.round((e.loaded / e.total) * 100) });
                }
              };
              uploadXhr.upload.onload = () => setProgress({ phase: 'opening' });

              uploadXhr.onload = () => {
                if (uploadXhr.status >= 300) {
                  fail(new Error(`S3 upload failed: HTTP ${uploadXhr.status}`));
                  return;
                }
                // ── Step 3: trigger SSE stream from s3_key ─────────────────
                streamSse(
                  `${apiBaseUrl}/api/count-images-s3`,
                  JSON.stringify({ s3_key }),
                  'application/json',
                );
              };
              uploadXhr.onerror = () => fail(new Error('Network error'));
              uploadXhr.send(file);
            });
          } else if (initResp.status === 501) {
            // S3 not available (local dev) — fall back to direct upload
            if (file.size > MAX_DIRECT_SIZE) {
              fail(new Error(
                `File too large (${Math.round(file.size / 1024 / 1024)} MB). ` +
                `Maximum size for local mode is 9 MB.`
              ));
              return;
            }
            const formData = new FormData();
            formData.append('file', file);
            streamSse(`${apiBaseUrl}/api/count-images`, formData, null);
          } else {
            fail(new Error(`HTTP ${initResp.status}`));
          }
        })
        .catch((err) => fail(err));
    });
  }, [apiBaseUrl]);

  return { countImages, isCountingImages, progress };
}

export default useImageCount;
